import { useEffect, useRef, useState } from 'react';

/**
 * Returns true only when the content is actually cut off (e.g. by a
 * `-webkit-line-clamp` or `overflow: hidden` truncation). Measured rather
 * than assumed, because showing a tooltip on a fully visible value is just
 * noise.
 *
 * Pass `content` when the same element instance can have its text replaced
 * without a size change (e.g. a memoized table cell) — a ResizeObserver alone
 * won't fire in that case since the observed box didn't resize.
 */
export const useIsClamped = <T extends HTMLElement>(content?: unknown) => {
  const ref = useRef<T>(null);
  const [isClamped, setIsClamped] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const checkTruncation = () => {
      // Covers both multi-line `-webkit-line-clamp` (vertical overflow) and
      // single-line `white-space: nowrap` + ellipsis (horizontal overflow).
      setIsClamped(
        element.scrollHeight > element.clientHeight + 1 ||
          element.scrollWidth > element.clientWidth + 1
      );
    };

    checkTruncation();

    if (typeof ResizeObserver === 'undefined') return;
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(checkTruncation);
    });
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [content]);

  return { ref, isClamped };
};
